import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Container, Alert, Form } from 'react-bootstrap';
import usePayrollData from './hooks/usePayrollData';
import SearchFilters from './components/SearchFilters';
import LoadingModal from './components/LoadingModal';
import DataTable from '../widgets/DataTable';
import ActionButtons from './components/ActionButtons';
import StatusBadge, { ESTADOS_NOMINA } from './components/StatusBadge';
import ManualNomina from './components/ManualNomina';
import ProgresoPeriodo, { GRUPOS_ESTADO } from './components/ProgresoPeriodo';
import { BsInfoCircle } from 'react-icons/bs/index.js';
import Modalobs from '../subcomponent/modalObservacion';
import funciones from '../../middleware/funciones';
// Mismo azul y tarjeta de título de la Auditoría General de Ingresos (.ai-root, --ai-grad)
import '../configuracion/auditoriaIngresos.css';

const GestionesNomina = () => {
  const {
    // Estados
    selectedItems,
    loading,
    visObsPDF,
    filters,
    searchFilters,
    authData,
    documentos,
    filteredDocuments,

    // Funciones de acciones
    updateInfo,
    getPDF,
    mailerDoc,
    deletedoc,
    enviarPayroll,
    confirmPayroll,
    limpiarNomina,

    // Funciones de selección
    isSelected,
    handleSelectAll,
    handleCheckboxChange,
    massiveApprove,
    approving,

    // Funciones de filtros
    ultimosPeriodos,
    periodosConNomina,
    selectPeriodo,
    updateFilter,
    updateSearchFilter,

    // Funciones de UI
    setVisObsPDF,
  } = usePayrollData();

  const [visibleRows, setVisibleRows] = useState([]);
  const [verManual, setVerManual] = useState(false);
  // Grupo de estado elegido en la barra de avance (filtra la tabla); se limpia al cambiar de periodo
  const [grupo, setGrupo] = useState(null);
  useEffect(() => { setGrupo(null); }, [filters.periodo]);
  const estadosGrupo = grupo ? GRUPOS_ESTADO.find((g) => g.key === grupo).estados : null;
  const datosTabla = estadosGrupo
    ? filteredDocuments.filter((d) => estadosGrupo.indexOf(String(d.DOCUMENTO_ENVIADO)) !== -1)
    : filteredDocuments;

  const handleFilteredDataChange = useCallback((rows) => {
    setVisibleRows(prev => {
      if (prev.length !== rows.length) return rows;
      for (let i = 0; i < rows.length; i++) {
        if (prev[i] !== rows[i]) return rows;
      }
      return prev;
    });
  }, []);

  // Manejadores de eventos
  const handleFilterChange = (name, value) => updateFilter(name, value);
  const handleSearch = (e) => updateInfo(e, true);

  const columns = useMemo(() => [
    {
      key: 'seleccion',
      label: (
        <Form.Check
          type="checkbox"
          onChange={(e) => handleSelectAll(e, visibleRows)}
          checked={selectedItems.length === visibleRows.length && visibleRows.length > 0}
        />
      ),
      sortable: false,
      style: { width: '30px', padding: '0 8px', textAlign: 'center' },
      render: (_, row) => (
        <Form.Check
          type="checkbox"
          id={row.ID_CABECERA_NOMINA}
          checked={isSelected(row.ID_CABECERA_NOMINA)}
          onChange={(e) => handleCheckboxChange(e, row)}
        />
      )
    },
    {
      key: 'FECHA_DOCUMENTO',
      label: 'Fecha',
      sortable: true,
      render: (val) => funciones.formatDate(val),
      searchValue: (val) => funciones.formatDate(val),
      style: { minWidth: '120px' }
    },
    {
      key: 'TIPO_DOCUMENTO_NOMINA',
      label: 'Prefijo',
      sortable: true,
      render: (val) => <span className="nom-pill">{val}</span>,
      style: { minWidth: '40px', maxWidth: '80px', padding: '0 8px' }
    },
    {
      key: 'NUMERO_DOCUMENTO_NOMINA',
      label: 'Número',
      sortable: true,
      render: (val) => <strong>{val}</strong>,
      style: { minWidth: '80px', maxWidth: '120px' }
    },
    {
      key: 'NOMBRE_EMPLEADO',
      label: 'Empleado',
      sortable: true,
      style: { minWidth: '260px' }
    },
    {
      key: 'ID_TRANSACCION',
      label: 'Transacción',
      sortable: true,
      style: { minWidth: '100px', maxWidth: '160px' }
    },
    {
      key: 'TELEFONO_EMPRESA',
      label: 'Periodo',
      sortable: true,
      style: { minWidth: '80px', maxWidth: '120px' }
    },
    {
      key: 'DOCUMENTO_ENVIADO',
      label: 'Estado',
      sortable: true,
      style: { minWidth: '100px', maxWidth: '150px' },
      render: (val) => <StatusBadge estado={val} />,
      searchValue: (val) => ESTADOS_NOMINA[val] || "Desconocido"
    },
    {
      key: 'DOCUMENTO_ENVIADO_OBSERVACION',
      label: 'Observaciones',
      sortable: false,
      style: { width: '150px', minWidth: '180px', maxWidth: '180px' },
      render: (val) => (
        <div style={{ width: '150px', display: 'block' }}>
          <Modalobs
            text={funciones.textCut(val)}
            descripcion={val}
          />
        </div>
      )
    },
    {
      key: 'acciones',
      label: 'Acciones',
      sortable: false,
      style: { width: '160px', textAlign: 'center' },
      render: (_, row) => (
        <ActionButtons
          currentData={row}
          onEnviarPayroll={enviarPayroll}
          onConfirmPayroll={confirmPayroll}
          onGetPDF={getPDF}
          onMailerDoc={mailerDoc}
          onDeleteDoc={deletedoc}
          onReprocess={limpiarNomina}
        />
      )
    }
  ], [handleSelectAll, handleCheckboxChange, isSelected, enviarPayroll, confirmPayroll, getPDF, mailerDoc, limpiarNomina, selectedItems, visibleRows]);

  return (
    <div className="Menu ai-root nom-root" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', paddingBottom: '2rem' }}>
      {/* Tarjeta de título (estándar de la Auditoría General de Ingresos) */}
      <Container fluid className="pt-0 px-0">
        <div style={{
          background: 'var(--ai-grad)',
          color: 'white',
          padding: '1.4rem 2rem',
          borderRadius: '16px',
          boxShadow: '0 4px 14px rgba(27, 47, 85, 0.25)',
          textAlign: 'left',
          position: 'relative'
        }}>
          <button type="button" className="nom-btn-info" onClick={() => setVerManual(true)} aria-label="Cómo usar Gestión de Nóminas" title="Cómo usar Gestión de Nóminas">
            <BsInfoCircle />
          </button>
          <h2 style={{ color: 'white', margin: 0, fontWeight: '700', fontSize: '1.5rem', letterSpacing: '-0.4px' }}>
            Gestión de Nóminas
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
            Aprobación y seguimiento de documentos de nómina electrónica
          </p>
        </div>
      </Container>

      {/* Validación de sesión */}
      {!authData.isLogin && (
        <Container>
          <Alert variant="danger">
            <Alert.Heading>Sesión Expirada</Alert.Heading>
            <p>La sesión se ha cerrado. Por favor, ingrese de nuevo.</p>
          </Alert>
        </Container>
      )}

      {/* Contenido principal */}
      {authData.isLogin && (
        <Container fluid className="px-3" style={{ marginTop: '20px' }}>
          <div className="dt-wrapper" style={{ overflow: 'visible', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 24px rgba(0, 0, 0, 0.06)' }}>
            <SearchFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              periodos={ultimosPeriodos}
              periodosConNomina={periodosConNomina}
              onSelectPeriodo={selectPeriodo}
              onSearch={handleSearch}
              onMassiveApprove={massiveApprove}
              approving={approving}
              selectedItems={selectedItems}
              searchFilters={searchFilters}
              onSearchChange={updateSearchFilter}
            >
              <ProgresoPeriodo periodo={filters.periodo} documentos={documentos} grupo={grupo} onGrupo={setGrupo} />
            </SearchFilters>
          </div>

          {/* Al recargar se dejan las filas atenuadas en vez de "Cargando…": la tabla no se encoge y la pantalla no salta */}
          <div className={loading && filteredDocuments.length > 0 ? 'nom-tabla-cargando' : ''}>
            <DataTable
              data={datosTabla}
              columns={columns}
              loading={loading && filteredDocuments.length === 0}
              onFilteredDataChange={handleFilteredDataChange}
              searchKeys={['FECHA_DOCUMENTO', 'TIPO_DOCUMENTO_NOMINA', 'NUMERO_DOCUMENTO_NOMINA', 'NOMBRE_EMPLEADO', 'ID_TRANSACCION', 'TELEFONO_EMPRESA', 'DOCUMENTO_ENVIADO', 'DOCUMENTO_ENVIADO_OBSERVACION']}
              searchPlaceholder="Búsqueda rápida (Nombre, Número, etc.)..."
              emptyText={
                <span style={{ color: '#7c8791ff', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.8rem' }}>
                  No se encontraron documentos de nómina con los filtros aplicados
                </span>
              }
              defaultItemsPerPage={100}
              rowClassName={(row) => row.VALID !== 0 ? 'dt-row-error' : ''}
              topRightContent={
                <Form.Control
                  as="select"
                  className="dt-custom-select"
                  size="sm"
                  name="searchEstado"
                  value={(searchFilters && searchFilters.searchEstado) || ''}
                  onChange={updateSearchFilter}
                  style={{ borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', color: (searchFilters && searchFilters.searchEstado) ? '#111827' : '#6b7280', width: '180px', fontSize: '0.85rem' }}
                >
                  <option value="">Estado: Todos</option>
                  <option value="0">En Proceso</option>
                  <option value="1">Pendiente DIAN</option>
                  <option value="2">Exitoso DIAN</option>
                  <option value="3">Error DIAN</option>
                  <option value="5">Por Aprobación</option>
                </Form.Control>
              }
            />
          </div>
        </Container>
      )}

      {/* Modal de carga */}
      <ManualNomina show={verManual} onHide={() => setVerManual(false)} />

      <LoadingModal
        show={visObsPDF}
        onHide={() => setVisObsPDF(false)}
      />
    </div>
  );
};

export default GestionesNomina;


// WEBPACK FOOTER //
// ./src/components/nomina/gestionesNomina.jsx